import { onBeforeUnmount, reactive, watchEffect, type MaybeRefOrGetter, toValue } from 'vue'

/**
 * Title of the current page. By default the header shows the route's nav
 * label and subtitle; a page overrides them (a contact's name, a script's
 * title) for as long as it is mounted.
 *
 * Actions and filters go through `<Teleport defer to="#page-actions">` and
 * `<Teleport defer to="#page-filters">`. `defer` matters: a page mounted in
 * the same render as the shell (a first load, a login) would otherwise look
 * for the target before the shell has put it in the document.
 */
export const pageHeader = reactive<{ title: string | null; subtitle: string | null }>({ title: null, subtitle: null })

export function usePageTitle(title: MaybeRefOrGetter<string | null>, subtitle?: MaybeRefOrGetter<string | null>): void {
  const mine: { title: string | null; subtitle: string | null } = { title: null, subtitle: null }
  watchEffect(() => {
    mine.title = toValue(title)
    mine.subtitle = subtitle === undefined ? null : toValue(subtitle)
    pageHeader.title = mine.title
    pageHeader.subtitle = mine.subtitle
  })
  // Before unmount, not after: the next page's setup runs in between and has
  // already set its own title. And only what this page set is cleared.
  onBeforeUnmount(() => {
    if (pageHeader.title === mine.title && pageHeader.subtitle === mine.subtitle) {
      pageHeader.title = null
      pageHeader.subtitle = null
    }
  })
}
